import globalLogger from "@lazarentertainment/js-logger"
import ClientOAuth2 from "client-oauth2"
import jwtDecode from "jwt-decode"
import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react"
import { isSafari } from "react-device-detect"
import { v4 } from "uuid"

import { useConfigContext } from "../configuration/ConfigContext"

import {
  clearAccessToken,
  clearRefreshToken,
  getAccessToken,
  isEmbedded,
  refreshTokens,
} from "./helpers"

const handleWindowClose = ({
  window,
  handler,
  pollInterval = 500,
}: {
  window: Window
  handler: () => void
  pollInterval?: number
}) => {
  const timer = setInterval(() => {
    if (window.closed) {
      clearInterval(timer)
      handler()
    }
  }, pollInterval)
}

interface AccessTokenBody {
  exp: number
  iat: number
  iss: string
  sub: string
  displayName?: string // only defined on user tokens
  permissions: {
    account: [{ [key: string]: string }]
    channel: [{ [key: string]: string }]
  }
  roles: [{ [key: string]: string }]
  scopes: string
}

interface Props {
  channelId: string
  children?: ReactNode
}

interface User {
  userId: string
  isChannelOwner: boolean
  channelId?: string
}

interface State {
  user: User | null
}

interface Context {
  auth: State
  refreshAccessToken: () => Promise<void>
  openSignInWindow: () => void
  openSignUpWindow: () => void
  signOut: () => void
  requestSafariStorageAccess: () => void
  shouldUseLocalStorageForAuth: boolean
}

const getUser = (accessToken: string, currentChannelId: string): User => {
  const decodedToken = jwtDecode<AccessTokenBody>(accessToken)
  const userId = decodedToken.sub
  const channelId = decodedToken.roles
    .filter((role) => role.type === "channel" && role.role === "owner")
    .map((role) => role.id)[0]

  return {
    userId,
    channelId,
    isChannelOwner: channelId === currentChannelId,
  }
}

const AuthContext = createContext<Context | undefined>(undefined)

const AuthProvider: FC<Props> = ({ channelId, children }) => {
  const config = useConfigContext()
  const [accessToken, setAccessToken] = useState<string>(getAccessToken())
  const [state, setState] = useState<State>({ user: null })

  const [oauthClient, setOauthClient] = useState<ClientOAuth2>(null)

  useEffect(() => {
    // `useEffect` will only execute client-side ensuring that `window` is defined
    setOauthClient(
      new ClientOAuth2({
        ...config.stageten,
        accessTokenUri: config.stageten.accessTokenUri,
        authorizationUri: config.stageten.authorizationUri,
        checkTokenUri: config.stageten.checkTokenUri,
        clientId: config.stageten.clientId,
        redirectUri: config.stageten.redirectUri,
      }),
    )
  }, [])

  useEffect(() => {
    if (accessToken) {
      const user = getUser(accessToken, channelId)
      setState({ user })
    }
  }, [accessToken])

  const openSignInWindow = () => {
    const authUri = oauthClient.code.getUri({
      state: v4(),
      query: { action: "signin", prompt: "login" },
    })

    const authWindow = window.open(authUri, "_blank")

    handleWindowClose({
      window: authWindow,
      handler: () => {
        const token = getAccessToken()
        if (token) setAccessToken(token)
        else window.location.reload()
      },
    })
  }

  const openSignUpWindow = () => {
    const authUri = oauthClient.code.getUri({
      state: v4(),
      query: { action: "signup" },
    })

    const authWindow = window.open(authUri, "_blank")

    handleWindowClose({
      window: authWindow,
      handler: () => {
        const token = getAccessToken()
        if (token) setAccessToken(token)
        else window.location.reload()
      },
    })
  }

  const signOut = () => {
    setAccessToken(null)
    setState({ user: null })
    clearAccessToken()
    clearRefreshToken()
  }

  const refreshAccessToken = async () => {
    await refreshTokens({
      tokenRefreshUri: config.stageten.tokenRefreshUri,
    })
      .then((tokens) => {
        // Update react state
        setAccessToken(tokens.accessToken)
      })
      .catch((err) => {
        globalLogger.error("Error during token refresh, signing out:", err)
        signOut()
      })
  }

  const shouldUseLocalStorageForAuth = isSafari && isEmbedded

  const requestSafariStorageAccess = () => {
    if (shouldUseLocalStorageForAuth) {
      // Cast required since `requestStorageAccess` is an experimental API
      ;(document as any).requestStorageAccess().catch(() => {
        /** Ignore errors */
      })
    }
  }

  return (
    <AuthContext.Provider
      value={{
        auth: state,
        refreshAccessToken,
        openSignInWindow,
        openSignUpWindow,
        signOut,
        requestSafariStorageAccess,
        shouldUseLocalStorageForAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

const useAuthContext = () => {
  const authContext = useContext(AuthContext)
  if (!authContext) {
    throw new Error(
      "No AuthContext.Provider found when calling useAuthContext.",
    )
  }
  return authContext
}

export { AuthContext, AuthProvider, useAuthContext }
